// @rpg-unit/music-mood-presets: the musical vocabulary for @rpg/audio's music director, by feeling (tier 3; composition
// framework, decision 0077). Cut from spike 002's music pass (2026-10-19):
//   - three moods: `explore` (calm, hopeful, pastoral: 84 bpm, D major pentatonic), `town` (bustling, bright: 100 bpm,
//     D major, a walking bass) and `night` (wistful, quiet: 60 bpm, D dorian), one key centre so a change never
//     clashes; each with its chords and named progressions, an A A' B A' form of a two-bar motif, its parts (lead, pad,
//     arpeggio, bass) entering over the first bars, a swell and humanising;
//   - a fight layer: `tresillo` (urgent, driving: 3+3+2 on 16 steps);
//   - a sting: `victory-bell` (triumphant: A D F# B D over the tonic).
// Moods and layers name their instruments by id and the band (an ensemble preset) they were written for, as data: a
// preset may depend only on packages and recipes, so the feature that plays them (adaptive music) resolves the ids.
//
// A game takes them as content (`include.json`, or `MUSIC_MOOD_PRESET_CONTENT` in the browser: the `music-moods`,
// `music-combat` and `music-stings` tables), as the director's types (`toMood`, `toCombat`, `toVictory`), as a vocabulary
// to compose from (`moodVocabulary`: progressions, patterns, rhythm cells and forms by feeling), and tuned by knobs
// (`tuneMood`: pace, key, looseness, swell).
import { MusicDirector, musicChecks, type MusicChecks, type MusicMood, type MusicTable, type NoteEvent, type ChordMark } from "@rpg/audio";
import moodsTable from "./content/tables/music-moods.json";
import combatTable from "./content/tables/music-combat.json";
import stingsTable from "./content/tables/music-stings.json";
import moodsSchema from "./content/schemas/music-moods.schema.json";
import combatSchema from "./content/schemas/music-combat.schema.json";
import stingsSchema from "./content/schemas/music-stings.schema.json";

/** This unit's version (the manifest's and package.json's). */
export const MUSIC_MOOD_PRESETS_VERSION = "0.1.0" as const;

/** A mood preset: the director's mood plus what it feels like, the band it was written for and why. */
export type MoodPreset = MusicMood & { feeling: string[]; ensemble: string; reason: string };
/** A fight layer preset: the director's combat layer, each part with its role in the band. */
export interface CombatPreset {
  id: string;
  feeling: string[];
  ensemble: string;
  parts: (MusicTable["combat"]["parts"][number] & { role: "drum" | "shaker" | "bass" | "fill" })[];
  hold: number;
  fadeOut: number;
  reason: string;
}
/** A sting preset: the director's victory phrase. */
export type StingPreset = MusicTable["victory"] & { id: string; kind: "victory"; feeling: string[]; ensemble: string; reason: string };

/** The moods (`{ table: "music-moods", version, rows }`). */
export const MUSIC_MOODS_TABLE = moodsTable as unknown as { table: "music-moods"; version: number; rows: MoodPreset[] };
/** The fight layers (`music-combat`). */
export const MUSIC_COMBAT_TABLE = combatTable as unknown as { table: "music-combat"; version: number; rows: CombatPreset[] };
/** The stings (`music-stings`). */
export const MUSIC_STINGS_TABLE = stingsTable as unknown as { table: "music-stings"; version: number; rows: StingPreset[] };

/** The content this unit adds to a game, in @rpg/content's shape: its tables and their schemas by table name. */
export const MUSIC_MOOD_PRESET_CONTENT = {
  package: "@rpg-unit/music-mood-presets",
  version: MUSIC_MOOD_PRESETS_VERSION,
  tables: [MUSIC_MOODS_TABLE, MUSIC_COMBAT_TABLE, MUSIC_STINGS_TABLE],
  schemas: { "music-moods": moodsSchema as Record<string, unknown>, "music-combat": combatSchema as Record<string, unknown>, "music-stings": stingsSchema as Record<string, unknown> },
} as const;

/** A mood preset as the director's mood (its feeling, band and reason left off; every musical field as it is). */
export function toMood(row: MoodPreset): MusicMood {
  const { feeling: _f, ensemble: _e, reason: _r, ...mood } = row;
  return mood;
}
/** A fight layer preset as the director's combat layer (the parts' roles left off). */
export function toCombat(row: CombatPreset): MusicTable["combat"] {
  return { parts: row.parts.map(({ role: _role, ...p }) => p), hold: row.hold, fadeOut: row.fadeOut };
}
/** A sting preset as the director's victory phrase. */
export function toVictory(row: StingPreset): MusicTable["victory"] {
  return { inst: row.inst, notes: row.notes, step: row.step, vel: row.vel };
}

/** The perceptual knobs (rule 13). */
export const MUSIC_MOOD_KNOBS = {
  pace: "faster or slower: scales the tempo (1 as tuned; 0.9 a little slower)",
  key: "higher or lower: moves the key centre by semitones, every part's range with it (a pure transposition)",
  looseness: "tighter or looser: scales the humanising (timing and velocity); 0 is machine-exact",
  swell: "more or less dynamic: scales the swell over each section; 0 is flat",
} as const;

/** Knob values; each defaults to as tuned. */
export interface MoodKnobs {
  pace?: number;
  key?: number;
  looseness?: number;
  swell?: number;
}

const round = (x: number): number => Math.round(x * 1e6) / 1e6;

/** A mood turned by the knobs (a new row; the input is untouched). With no knobs it equals the input. */
export function tuneMood<T extends MusicMood>(row: T, knobs: MoodKnobs = {}): T {
  const { pace = 1, key = 0, looseness = 1, swell = 1 } = knobs;
  if (!(pace > 0) || !(looseness >= 0) || !(swell >= 0) || !Number.isInteger(key)) throw new Error(`music moods: pace > 0, looseness and swell ≥ 0, key a whole number of semitones (got ${JSON.stringify(knobs)})`);
  const out = structuredClone(row);
  if (pace !== 1) out.bpm = round(out.bpm * pace);
  // a key change moves every part with it: the root and each part's range (a range left behind clips the motif's
  // contour, and the theme stops coming back the same)
  if (key !== 0) {
    out.root += key;
    if (out.lead) out.lead = { ...out.lead, low: out.lead.low + key, high: out.lead.high + key };
    if (out.pad?.range) out.pad = { ...out.pad, range: [out.pad.range[0] + key, out.pad.range[1] + key] };
    if (out.arp) out.arp = { ...out.arp, range: [out.arp.range[0] + key, out.arp.range[1] + key] };
  }
  if (looseness !== 1 && out.humanise) out.humanise = { time: round(out.humanise.time * looseness), vel: round(out.humanise.vel * looseness) };
  if (swell !== 1 && out.swell !== undefined) out.swell = round(out.swell * swell);
  return out;
}

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
/** A pitch class's name (sharps). */
export const noteName = (pc: number): string => NOTE_NAMES[((pc % 12) + 12) % 12]!;

/** A chord's name from its tones (semitones above the key's root, the chord's root first): Dadd9, Bm7, Gmaj7, A6,
 *  G6/9, Asus4, A7sus4, Dm9. */
export function chordName(keyRoot: number, tones: readonly number[]): string {
  const r = tones[0]!;
  const iv = new Set(tones.map((t) => (((t - r) % 12) + 12) % 12));
  const ext = new Set(tones.map((t) => t - r));
  const root = noteName(keyRoot + r);
  const minor = iv.has(3) && !iv.has(4);
  const major = iv.has(4);
  const nine = iv.has(2) || ext.has(14);
  const sev = iv.has(11) ? "maj7" : iv.has(10) ? "7" : "";
  if (!major && !minor) {
    const sus = iv.has(5) ? "sus4" : iv.has(2) ? "sus2" : "5";
    return `${root}${sev === "7" ? "7" : sev}${sus}`;
  }
  const q = minor ? "m" : "";
  if (sev) return nine ? `${root}${q}${sev === "maj7" ? "maj9" : "9"}` : `${root}${q}${sev}`;
  if (iv.has(9)) return nine ? `${root}${q}6/9` : `${root}${q}6`;
  return nine ? `${root}${q}add9` : `${root}${q}`;
}

/** A scale's name, where it has a common one. */
export function scaleName(scale: readonly number[]): string {
  const k = scale.join(",");
  return ({ "0,2,4,7,9": "major pentatonic", "0,3,5,7,10": "minor pentatonic", "0,2,4,5,7,9,11": "major", "0,2,3,5,7,8,10": "natural minor", "0,2,3,5,7,9,10": "dorian", "0,2,4,5,7,9,10": "mixolydian", "0,2,4,6,7,9,11": "lydian" } as Record<string, string>)[k] ?? `scale ${k}`;
}

/** One entry of the vocabulary: where it came from and how it feels. */
export interface VocabEntry<T> {
  from: string;
  feeling: string[];
  value: T;
}

/** The musical vocabulary the moods hold, by feeling: their progressions (named chords), arpeggio and bass patterns,
 *  rhythm cells and forms, each tagged with the feelings of the mood it came from, so a composer can borrow a
 *  progression or a pattern for a new mood that should feel the same way. */
export function moodVocabulary(rows: readonly MoodPreset[] = MUSIC_MOODS_TABLE.rows) {
  const progressions: VocabEntry<{ name: string; chords: string[]; barsPerChord: number; tones: number[][] }>[] = [];
  const arpPatterns: VocabEntry<{ pattern: number[]; step: number }>[] = [];
  const bassPatterns: VocabEntry<{ beats: number[]; notes: number[]; walk: boolean }>[] = [];
  const cells: VocabEntry<number[][]>[] = [];
  const forms: VocabEntry<string>[] = [];
  for (const m of rows) {
    const tag = { from: m.id, feeling: m.feeling };
    const named: [string, number[][]][] = [["A", m.progression], ...Object.entries(m.progressions ?? {})];
    for (const [name, tones] of named) progressions.push({ ...tag, value: { name: `${m.id}.${name}`, chords: tones.map((t) => chordName(m.root, t)), barsPerChord: m.barsPerChord ?? 1, tones } });
    if (m.arp) arpPatterns.push({ ...tag, value: { pattern: m.arp.pattern, step: m.arp.step } });
    if (m.bass) bassPatterns.push({ ...tag, value: { beats: m.bass.beats, notes: m.bass.notes ?? m.bass.beats.map(() => 0), walk: !!m.bass.walk } });
    if (m.lead) cells.push({ ...tag, value: m.lead.cells });
    if (m.form) forms.push({ ...tag, value: m.form.map((s) => `${s.chords}${s.lead ? `/${s.lead}` : "/–"}${s.inst ? ` (${s.inst})` : ""}${s.rest?.length ? ` [no ${s.rest.join(", ")}]` : ""}`).join("  ") });
  }
  return { progressions, arpPatterns, bassPatterns, cells, forms };
}

/** The director over one mood (with a fight layer and a sting for its table), on a fake clock: every note, the chords as
 *  they sounded, and @rpg/audio's music checks over the run. `script` runs before each 50 ms step (an alert, a win). */
export function playMood(mood: MusicMood, o: { combat?: MusicTable["combat"]; victory?: MusicTable["victory"]; seed?: number; bars?: number; script?: (d: MusicDirector, t: number) => void } = {}): { notes: NoteEvent[]; chords: ChordMark[]; checks: MusicChecks; barLength: number } {
  const notes: NoteEvent[] = [];
  const table: MusicTable = { moods: [mood], combat: o.combat ?? toCombat(MUSIC_COMBAT_TABLE.rows[0]!), victory: o.victory ?? toVictory(MUSIC_STINGS_TABLE.rows[0]!) };
  const d = new MusicDirector(table, { note: (e) => notes.push(e), fade: () => undefined }, { seed: o.seed ?? 11, mood: mood.id });
  const L = d.barLength();
  const bars = o.bars ?? 64;
  for (let s = 0; s < L * bars + 1; s += 0.05) {
    o.script?.(d, s);
    d.update(s);
  }
  // the window ends just before the bar line after the last bar: that bar line's grid can land a rounding error early
  // (bar lines are sums of bar lengths), and a statement cut to its first note would read as a theme that changed
  return { notes, chords: [...d.chords], checks: musicChecks(notes, d.chords, mood, 0, L * bars - 1e-6), barLength: L };
}

/** The bounds a mood's music is held to (the music pass's done-whens): any pad voice moves at most `maxMove` semitones
 *  between chords and `meanMove` on average; every repeat and restatement of a motif matches; no lead note outside the
 *  scale and no pad or arpeggio note outside its chord; each part enters at or after its bar, the pad first; the pad
 *  swells at least as asked and the humanising stays inside its bounds; a mood breathes (a swell of at least
 *  `minSwell`, humanising of at least `minHumanise` seconds). */
export const MOOD_BOUNDS = { maxMove: 6, meanMove: 3, minSwell: 0.05, minHumanise: 0.005, stingBars: 2 } as const;

/** What's wrong with a mood's music when played (empty: within the bounds): `playMood` for 64 bars, read by the checks. */
export function musicIssues(mood: MusicMood, o: { seed?: number } = {}): string[] {
  const out: string[] = [];
  const { checks: c } = playMood(mood, { seed: o.seed ?? 11 });
  const id = mood.id;
  if (c.voiceLeading.changes < 8 || c.voiceLeading.max > MOOD_BOUNDS.maxMove || c.voiceLeading.mean > MOOD_BOUNDS.meanMove) out.push(`${id}: voice leading ${JSON.stringify(c.voiceLeading)} (at most ${MOOD_BOUNDS.maxMove} semitones, ${MOOD_BOUNDS.meanMove} on average)`);
  if (mood.form && mood.lead && (c.motif.repeats < 1 || c.motif.repeatsMatching !== c.motif.repeats || c.motif.restatementsMatching !== c.motif.restatements)) out.push(`${id}: the motif doesn't recur ${JSON.stringify(c.motif)}`);
  if (c.harmony.outOfScale || c.harmony.outOfChord) out.push(`${id}: ${c.harmony.outOfScale} lead notes outside the scale, ${c.harmony.outOfChord} pad and arpeggio notes outside their chord (of ${c.harmony.checked})`);
  for (const part of ["pad", "bass", "arp", "lead"] as const) {
    const from = (mood[part] as { from?: number } | undefined)?.from ?? 0;
    const at = c.entries[part];
    if (mood[part] && (at === undefined || at < from - 0.01)) out.push(`${id}: the ${part} enters at bar ${at}, before its bar ${from}`);
  }
  if (mood.pad && c.entries.pad !== 0) out.push(`${id}: the pad isn't first (bar ${c.entries.pad})`);
  if ((mood.swell ?? 0) < MOOD_BOUNDS.minSwell || (mood.humanise?.time ?? 0) < MOOD_BOUNDS.minHumanise) out.push(`${id}: doesn't breathe (swell ${mood.swell ?? 0}, humanising ${mood.humanise?.time ?? 0} s)`);
  else if (c.dynamics.padVelRatio < 1 + mood.swell! - 1e-9 || c.dynamics.maxOffset > mood.humanise!.time + 1e-9) out.push(`${id}: dynamics ${JSON.stringify(c.dynamics)}`);
  return out;
}

/** What's wrong with the presets' structure (empty: sound), without playing them: unique ids; a form names only
 *  progressions the mood has; parts' ranges run low to high and the lead has an octave to move in; one key centre
 *  across the moods (a mood change never clashes); the moods distinct (tempo, scale, tune's instrument or harmonic
 *  rhythm); a fight layer's steps unique on the 16-step bar; a sting short enough to land inside `stingBars` bars. */
export function presetIssues(moods: readonly MoodPreset[], combat: readonly CombatPreset[] = [], stings: readonly StingPreset[] = []): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  for (const m of [...moods, ...combat, ...stings]) {
    if (ids.has(m.id)) out.push(`${m.id}: duplicate id`);
    ids.add(m.id);
  }
  for (const m of moods) {
    const names = new Set(["A", ...Object.keys(m.progressions ?? {})]);
    for (const s of m.form ?? []) if (!names.has(s.chords)) out.push(`${m.id}: its form names progression "${s.chords}", which it doesn't have`);
    if (m.lead && m.lead.high - m.lead.low < 12) out.push(`${m.id}: the lead's range ${m.lead.low}–${m.lead.high} is under an octave`);
    for (const part of ["pad", "arp"] as const) {
      const r = m[part]?.range;
      if (r && !(r[1] > r[0])) out.push(`${m.id}: the ${part}'s range ${r.join("–")} doesn't run low to high`);
    }
  }
  if (new Set(moods.map((m) => m.root)).size > 1) out.push(`moods on ${new Set(moods.map((m) => m.root)).size} key centres (${moods.map((m) => `${m.id} ${noteName(m.root)}`).join(", ")}): a change would clash`);
  const sig = moods.map((m) => `${m.bpm}|${m.scale.join(",")}|${m.lead?.inst}|${m.barsPerChord ?? 1}`);
  if (new Set(sig).size !== sig.length) out.push(`two moods sound alike (tempo, scale, tune's instrument, harmonic rhythm): ${sig.join("  ")}`);
  for (const c of combat) for (const p of c.parts) if (new Set(p.steps).size !== p.steps.length) out.push(`${c.id}: the ${p.role} hits a step twice`);
  for (const s of stings) {
    const beats = s.notes.length * s.step;
    const bar = Math.min(...moods.map((m) => m.beatsPerBar), 4);
    if (beats > MOOD_BOUNDS.stingBars * bar) out.push(`${s.id}: ${beats} beats long, more than ${MOOD_BOUNDS.stingBars} bars`);
  }
  return out;
}

/** A standard MIDI file (format 1, 480 ticks a beat) of the director's notes, one track per part (and the fight layer
 *  and the sting on their own), at `bpm` (the notes' times are seconds; any bpm keeps them, 60 makes a beat a second):
 *  a preview anyone can play. */
export function toMidi(notes: readonly NoteEvent[], bpm = 60): Uint8Array {
  const tpq = 480;
  const vlq = (n: number): number[] => {
    const b = [n & 0x7f];
    while ((n >>= 7)) b.unshift((n & 0x7f) | 0x80);
    return b;
  };
  const tick = (s: number): number => Math.max(0, Math.round((s * bpm * tpq) / 60));
  const partOf = (n: NoteEvent): string => (n.layer === "base" ? (n.part ?? "base") : n.layer);
  const parts = [...new Set(notes.map(partOf))];
  const track = (bytes: number[]): number[] => [0x4d, 0x54, 0x72, 0x6b, ...[24, 16, 8, 0].map((s) => (bytes.length >>> s) & 0xff), ...bytes];
  const tempo = Math.round(60_000_000 / bpm);
  const tracks = [track([0, 0xff, 0x51, 3, (tempo >> 16) & 0xff, (tempo >> 8) & 0xff, tempo & 0xff, 0, 0xff, 0x2f, 0])];
  parts.forEach((part, ch) => {
    const name = [...new TextEncoder().encode(part)];
    const evs: [number, number[]][] = [[0, [0xff, 0x03, name.length, ...name]]];
    for (const n of notes.filter((x) => partOf(x) === part)) {
      const v = Math.max(1, Math.min(127, Math.round(n.vel * 127)));
      evs.push([tick(n.at), [0x90 | ch, n.midi, v]], [tick(n.at + (n.dur ?? 0.25)), [0x80 | ch, n.midi, 0]]);
    }
    evs.sort((a, b) => a[0] - b[0] || (a[1][0]! & 0xf0) - (b[1][0]! & 0xf0));
    const bytes: number[] = [];
    let last = 0;
    for (const [t, e] of evs) {
      bytes.push(...vlq(t - last), ...e);
      last = t;
    }
    bytes.push(0, 0xff, 0x2f, 0);
    tracks.push(track(bytes));
  });
  const head = [0x4d, 0x54, 0x68, 0x64, 0, 0, 0, 6, 0, 1, (tracks.length >> 8) & 0xff, tracks.length & 0xff, (tpq >> 8) & 0xff, tpq & 0xff];
  return Uint8Array.from([...head, ...tracks.flat()]);
}
